import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Card, CardContent, CardHeader } from '@mui/material';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useModal } from 'mui-modal-provider';
import useLabeledProperties from '../../../../hooks/useLabeledProperties';
import { useClientFields } from '../../../../sic-lib/api/hooks/useClientFields';
import { useDeleteView } from '../../../../sic-lib/api/hooks/useViews';
import { View } from '../../../../sic-lib/types/View';
import DialogConfirmAction from '../../../Shared/DialogConfirmAction/DialogConfirmAction';
import IconButtonLoading from '../../../Shared/IconButtonLoading/IconButtonLoading';

interface Props {
    config: View;
    onToggleHandler: () => void;
}

function ClientViewsItem({ config, onToggleHandler }: Props) {
    const { showModal } = useModal();
    const { data: clientFields } = useClientFields();

    const { mutate: deleteViewMutation, isPending: deleteViewLoading } = useDeleteView();

    const displayProperties = useLabeledProperties(config.properties, clientFields || []);

    const handleShowConfirmDelete = () => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Delete View',
            description: 'This custom view will be permanently deleted',
            onConfirm: async () => deleteViewMutation(config.viewId),
            onCancel: () => {
                modal.hide();
            },
        });
    };

    return (
        <Card>
            <CardHeader
                title={config.name}
                action={
                    <Grid container direction="row" spacing={2}>
                        <Grid item>
                            <Tooltip title="Edit">
                                <IconButton
                                    onClick={onToggleHandler}
                                    className="clientViews__iconButton"
                                    size="large"
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <IconButtonLoading
                                tooltip="Remove"
                                showLoading={deleteViewLoading}
                                onClick={handleShowConfirmDelete}
                            >
                                <DeleteIcon />
                            </IconButtonLoading>
                        </Grid>
                    </Grid>
                }
            />
            <Divider />
            <CardContent>
                {displayProperties.length > 0 && (
                    <List disablePadding>
                        {displayProperties.map(prp => (
                            <ListItem key={prp.fieldId} disableGutters disablePadding>
                                <ListItemText
                                    primary={prp.label}
                                    secondary={prp.isPrimary && 'Filterable field'}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
                {displayProperties.length === 0 && (
                    <div className="clientViews__itemSection--message">
                        <Typography component="p" variant="body2" align="center">
                            No view properties
                        </Typography>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default ClientViewsItem;
